import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { FiEye } from "react-icons/fi";
import { HiHome } from "react-icons/hi";
import { FaRegBuilding, FaUser } from "react-icons/fa";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { capitalizeFirstLetter, getModeName } from "../../helpers";

const ViewCollectionModal = ({
  handleClose,
  selectedData,
  loading,
  collectionList
}) => {

  const firstItem = collectionList?.data?.[0];
  const inventoryName = firstItem?.inventoryName || selectedData?.inventoryName || "";
  const tenantName = firstItem?.tenantName || "";
  const address = selectedData?.address || "";

  return (
    <Container>
      <div className="flex items-center justify-between bg-gradient-to-r from-primary-color to-teal-400 py-4 px-4">
        <Title className="text-white">Collection Details</Title>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </div>

      {/* Profile Section - Shows Inventory and Tenant Info */}
      <ProfileSection className="px-4 py-3 flex items-center justify-between">
        {/* Left: Icon + Info */}
        <div className="flex items-center gap-2">
          {/* Inventory Icon */}
          <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
            <FaUser className="text-gray-600" size={20} />
          </div>

          {/* Text Info */}
          <div className="flex flex-col">
            <p className="text-md font-medium text-gray-800">
              {tenantName && `${tenantName}`}

            </p>
            <p className="text-xs text-gray-400">
              {capitalizeFirstLetter(inventoryName)}
            </p>
          </div>
        </div>

        {/* Right: Total Collections Count */}
        <div className="text-right">
          <p className="text-xs text-gray-400">Total Collections</p>
          <p className="text-lg font-bold text-green-600">
            ₹{collectionList?.total || 0}
          </p>
        </div>
      </ProfileSection>

      <Content>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-primary-color rounded-full animate-spin mb-3"></div>
            <p className="text-sm text-gray-500">Loading collections...</p>
          </div>
        ) : (
          <>
            {collectionList?.data?.length > 0 ? (
              collectionList.data.map((item, index) => {

                return (
                  <PropertyCard key={item.id || index}>
                    <CardHeader>
                      <PropertyInfo>
                        <InventoryName>
                          {item.title || item.name || "Collection"}
                        </InventoryName>
                        <InventoryType>
                          #{item.gId || "N/A"}

                        </InventoryType>
                      </PropertyInfo>
                      <div className="text-right">
                        <Value className="font-medium text-green-600 text-lg">
                          ₹{item.amount?.toLocaleString() || 0}
                        </Value>

                      </div>
                    </CardHeader>

                    <Divider />

                    <PropertyDetails>
                      <DetailRow>
                        <DetailItem>
                          <Label>Broker Share</Label>
                          <Value>₹{parseFloat(item.brokerShare || 0).toLocaleString()}</Value>
                        </DetailItem>
                        <DetailItem>
                          <Label>Mode</Label>
                          <Value className="text-xs">{getModeName(item.mode)}</Value>
                        </DetailItem>

                      </DetailRow>

                      <DetailRow>
                        <DetailItem>
                          <Label>Due Date</Label>
                          <Value>{moment(item.dueDate).format("DD MMM YYYY")}</Value>
                        </DetailItem>
                        <DetailItem>
                          <Label>Collection Date</Label>
                          <Value>{moment(item.collectionDate).format("DD MMM YYYY, hh:mm A")}</Value>
                        </DetailItem>

                      </DetailRow>
                      {/* 
                      {item.receipt && (
                        <div className="mt-2">
                          <a
                            href={item.receipt}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-color hover:underline flex items-center gap-1"
                          >
                            <FiEye size={12} />
                            View Receipt
                          </a>
                        </div>
                      )} */}

                      <MetaInfo className="flex justify-between items-center mt-3">
                        <span className="text-xs text-gray-400">
                          Recorded by: {item.recordedBy || "N/A"}
                        </span>
                        {/* <span className="text-xs text-gray-400">
                          Created: {moment(item.createdAt).format("DD MMM YY, hh:mm A")}
                        </span> */}
                        {item.receipt && (
                          <a
                            href={item.receipt}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-color hover:underline flex items-center gap-1"
                          >
                            <FiEye size={12} />
                            View Receipt
                          </a>)}
                      </MetaInfo>
                    </PropertyDetails>
                  </PropertyCard>
                );
              })
            ) : (
              <EmptyState className="flex flex-col items-center justify-center h-full text-center px-6">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <HiHome size={28} className="text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-700">
                  No collections found
                </p>
                <p className="text-xs text-gray-400 mt-1 max-w-[220px]">
                  No collection records available for this property
                </p>
              </EmptyState>
            )}
          </>
        )}
      </Content>
    </Container>
  );
};

// Styled Components
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-gray-600`;
const Container = tw.div`bg-white w-full h-full flex flex-col`;
const ProfileSection = tw.div`flex items-center justify-between bg-gray-100`;
const Content = tw.div`px-4 py-4 space-y-4 overflow-y-auto flex-1`;
const PropertyCard = tw.div`bg-white border border-gray-200 rounded-lg p-4`;
const CardHeader = tw.div`flex justify-between items-start mb-3`;
const PropertyInfo = tw.div`flex flex-col`;
const InventoryName = tw.div`text-base font-medium text-gray-800`;
const InventoryType = tw.div`text-sm text-gray-400`;
const Divider = tw.div`h-px bg-gray-100 my-3`;
const PropertyDetails = tw.div`space-y-3`;
const DetailRow = tw.div`grid grid-cols-2 gap-4`;
const DetailItem = tw.div`flex flex-col`;
const Label = tw.label`text-sm text-gray-400`;
const Value = tw.span`text-gray-600 text-sm`;
const MetaInfo = tw.div`flex justify-end mt-2`;

const EmptyState = tw.div`text-center text-gray-400 text-sm py-10`;

export default ViewCollectionModal;